import React, { useEffect, useRef } from "react"
import { useLocation } from "wouter"
import { gsap } from "gsap"
import { ScrollTrigger } from "gsap/ScrollTrigger"
import { CubeScene } from "../components/CubeScene"

gsap.registerPlugin(ScrollTrigger)

const heroText =
  "PresentFuture is a boutique studio at the intersection of leadership and innovation."

const interludeText =
  "Growing is rarely just about the product or the team. It's both, always, at once."

const section2Text =
  "Years designing innovation programmes and building tech products, brought together into one integrated practice."

export function Home() {
  const [, navigate] = useLocation()
  const heroWordsRef = useRef([])
  const interludeWordsRef = useRef([])
  const interludeSectionRef = useRef()
  const s2WordsRef = useRef([])
  const ctaRefs = useRef([])
  const s2Ref = useRef()
  const videoRef = useRef()


  useEffect(() => {
    const ctx = gsap.context(() => {
      // Hero animation on load
      gsap.fromTo(
        heroWordsRef.current,
        { opacity: 0, y: 10 },
        { opacity: 1, y: 0, duration: 0.9, ease: "power3.out", stagger: 0.055, delay: 0.3 }
      )

      // Interlude word animation on scroll
      gsap.fromTo(
        interludeWordsRef.current,
        { opacity: 0, y: 10 },
        {
          opacity: 1,
          y: 0,
          duration: 0.9,
          ease: "power3.out",
          stagger: 0.04,
          scrollTrigger: {
            trigger: interludeWordsRef.current[0],
            start: "top 80%",
          },
        }
      )

      // CTA links animation on scroll
      gsap.fromTo(
        ctaRefs.current,
        { opacity: 0, y: 16 },
        {
          opacity: 1,
          y: 0,
          duration: 0.8,
          ease: "power3.out",
          stagger: 0.12,
          scrollTrigger: { trigger: ctaRefs.current[0], start: "top 90%" },
        }
      )

      // Section 2 word animation on scroll
      gsap.fromTo(
        s2WordsRef.current,
        { opacity: 0, y: 10 },
        {
          opacity: 1,
          y: 0,
          duration: 0.9,
          ease: "power3.out",
          stagger: 0.055,
          scrollTrigger: {
            trigger: s2WordsRef.current[0],
            start: "top 80%",
          },
        }
      )
    })

    // Ping-pong video playback
    const video = videoRef.current
    let tween

    const startPingPong = () => {
      video.pause()
      tween = gsap.to(video, {
        currentTime: video.duration,
        duration: video.duration,
        ease: "none",
        repeat: -1,
        yoyo: true,
      })
    }

    if (video.readyState >= 1) {
      startPingPong()
    } else {
      video.addEventListener("loadedmetadata", startPingPong, { once: true })
    }

    return () => {
      ctx.revert()
      tween && tween.kill()
    }
  }, [])

  const heroWords = heroText.split(" ")
  const interludeWords = interludeText.split(" ")
  const s2Words = section2Text.split(" ")

  return (
    <div className="home">
      <section className="home-hero">
        <video ref={videoRef} className="hero-bg-video" src="/hero-bg.mp4" muted playsInline preload="auto" />
        <p className="hero-text">
          {heroWords.map((word, i) => (
            <span key={i} ref={el => (heroWordsRef.current[i] = el)} className="hero-word">
              {word}
              {i < heroWords.length - 1 ? " " : ""}
            </span>
          ))}
        </p>
      </section>

      <section className="home-interlude" ref={interludeSectionRef}>
        <CubeScene />
        <p className="interlude-text">
          {interludeWords.map((word, i) => (
            <span key={i} ref={el => (interludeWordsRef.current[i] = el)} className="interlude-word">
              {word}
              {i < interludeWords.length - 1 ? " " : ""}
            </span>
          ))}
        </p>
      </section>

      <section ref={s2Ref} className="home-section-2">
        <div className="s2-inner">
          <p className="hero-text">
            {s2Words.map((word, i) => (
              <span key={i} ref={el => (s2WordsRef.current[i] = el)} className="hero-word">
                {word}
                {i < s2Words.length - 1 ? " " : ""}
              </span>
            ))}
          </p>
          <div className="home-ctas">
            {[
              { label: "know us better", href: "/about" },
              { label: "explore our services", href: "/services" },
              { label: "get in touch", href: "/lets-work-together" },
            ].map((cta, i) => (
              <a
                key={i}
                ref={el => (ctaRefs.current[i] = el)}
                href={cta.href}
                className="home-cta"
                onClick={e => { e.preventDefault(); navigate(cta.href) }}
              >
                {cta.label}
              </a>
            ))}
          </div>
        </div>
      </section>
    </div>
  )
}
